import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api, formatApiError } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { MENU_LANG_META } from '../lib/menuLocale';
import { hasPlanFeature } from '../lib/plans';
import { PlanUpgradeNotice } from './PlanUpgradeNotice';
import { MenuFlag } from './MenuFlag';
import { Check } from 'lucide-react';

export function MenuLanguageSettings() {
    const { restaurant } = useAuth();
    const [available, setAvailable] = useState([]);
    const [enabled, setEnabled] = useState(['ro', 'en']);
    const [planFeatures, setPlanFeatures] = useState(null);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [retranslating, setRetranslating] = useState(false);
    const [retranslateNote, setRetranslateNote] = useState('');
    const pollRef = useRef(null);

    const stopPolling = useCallback(() => {
        if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
    }, []);

    const pollRetranslateStatus = useCallback(() => {
        stopPolling();
        pollRef.current = setInterval(async () => {
            try {
                const { data } = await api.get('/r/menu/retranslate/status');
                if (data.running) {
                    setRetranslating(true);
                    setRetranslateNote('Traducerea rulează în fundal. Poți naviga liber în aplicație.');
                    return;
                }
                stopPolling();
                setRetranslating(false);
                if (data.done) {
                    setRetranslateNote(
                        `Gata: ${data.categories} categorii, ${data.products} produse`
                        + (data.languages ? `, ${data.languages} limbi.` : '.')
                    );
                } else if (data.error) {
                    setRetranslateNote(`Eroare la traducere: ${data.error}`);
                } else {
                    setRetranslateNote('');
                }
            } catch {
                stopPolling();
                setRetranslating(false);
            }
        }, 3000);
    }, [stopPolling]);

    useEffect(() => {
        api.get('/r/restaurant/settings')
            .then((r) => {
                const ml = r.data.menu_languages;
                setAvailable(ml?.available || Object.entries(MENU_LANG_META).map(([code, meta]) => ({ code, ...meta })));
                setEnabled(ml?.enabled || ['ro', 'en']);
                setPlanFeatures(r.data.plan_features || restaurant?.plan_features || null);
            })
            .catch(() => {});

        api.get('/r/menu/retranslate/status')
            .then(({ data }) => {
                if (data.running) {
                    setRetranslating(true);
                    setRetranslateNote('Traducerea rulează în fundal. Poți naviga liber în aplicație.');
                    pollRetranslateStatus();
                }
            })
            .catch(() => {});

        return stopPolling;
    }, [pollRetranslateStatus, stopPolling]);

    const maxLangs = planFeatures?.limits?.max_menu_languages;
    const autoTranslate = hasPlanFeature({ plan_features: planFeatures }, 'menu_auto_translate')
        || hasPlanFeature(restaurant, 'menu_auto_translate');

    const toggle = (code) => {
        if (code === 'ro') return;
        setEnabled((prev) => {
            if (prev.includes(code)) return prev.filter((c) => c !== code);
            if (maxLangs != null && prev.length >= maxLangs) {
                alert(`Planul tău permite maximum ${maxLangs} limbi pentru meniu.`);
                return prev;
            }
            return [...prev, code];
        });
        setSaved(false);
    };

    const targetLangCount = enabled.filter((code) => code !== 'ro').length;

    const retranslateAll = async () => {
        if (!targetLangCount) {
            alert('Activează cel puțin o limbă în afară de română.');
            return;
        }
        setRetranslating(true);
        setRetranslateNote('Pornesc traducerea în fundal...');
        try {
            const { data } = await api.post('/r/menu/retranslate');
            if (data.started === false && data.running) {
                setRetranslateNote('Traducerea rulează deja în fundal.');
            } else {
                setRetranslateNote(data.message || 'Traducerea rulează în fundal. Poți continua să folosești aplicația.');
            }
            pollRetranslateStatus();
        } catch (e) {
            setRetranslating(false);
            setRetranslateNote('');
            alert(formatApiError(e));
        }
    };

    const save = async () => {
        setSaving(true);
        setSaved(false);
        try {
            const { data } = await api.patch('/r/restaurant/menu-languages', { menu_languages: enabled });
            setEnabled(data.menu_languages?.enabled || enabled);
            setSaved(true);
            if (data.retranslate?.started || data.retranslate?.running) {
                setRetranslating(true);
                setRetranslateNote('Limbile au fost salvate. Traducerea meniului rulează în fundal.');
                pollRetranslateStatus();
            }
        } catch (e) {
            alert(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="panel" style={{ maxWidth: 640, marginBottom: '2rem' }} data-testid="menu-language-settings">
            <div className="kicker">Meniu QR — limbi</div>
            <h2 className="headline" style={{ fontSize: '1.5rem', margin: '0.3rem 0 0.6rem' }}>
                Limbi pentru clienți
            </h2>
            <p style={{ color: 'var(--text-muted)', marginTop: 0, marginBottom: '1rem', fontSize: '0.92rem', lineHeight: 1.5 }}>
                Clienții pot alege limba meniului QR. Conținutul (nume, descrieri, categorii) se traduce
                <strong style={{ color: 'var(--amber)' }}> automat din română</strong> la salvare — nu trebuie completat manual.
                {maxLangs != null && <> Planul tău permite <strong style={{ color: '#fff' }}>{maxLangs} limbi</strong>.</>}
            </p>
            {!autoTranslate && <PlanUpgradeNotice featureLabel="Traducere automată meniu" compact />}
            <div className="menu-lang-settings-grid" style={{ marginBottom: '1rem' }}>
                {available.map(({ code, native }) => {
                    const isRo = code === 'ro';
                    const checked = enabled.includes(code);
                    return (
                        <label
                            key={code}
                            className={`menu-lang-settings-tile${checked ? ' menu-lang-settings-tile--on' : ''}${isRo ? ' menu-lang-settings-tile--locked' : ''}`}
                            title={native}
                            data-testid={`menu-lang-toggle-${code}`}
                        >
                            <input
                                type="checkbox"
                                checked={checked}
                                disabled={isRo}
                                onChange={() => toggle(code)}
                                className="menu-lang-settings-tile__input"
                            />
                            <MenuFlag code={code} size={40} />
                        </label>
                    );
                })}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                <button className="btn btn-primary" onClick={save} disabled={saving} data-testid="menu-lang-save">
                    {saving ? <span className="spinner" /> : <Check size={16} />}
                    {saved ? 'Salvat' : 'Salvează limbile'}
                </button>
                {autoTranslate && (
                <button className="btn btn-secondary" onClick={retranslateAll} disabled={retranslating} data-testid="menu-retranslate">
                    {retranslating ? <span className="spinner" /> : null}
                    {retranslating ? 'Traducere în fundal...' : 'Retraduce tot meniul'}
                </button>
                )}
            </div>
            {retranslateNote && (
                <p
                    style={{
                        marginTop: '0.85rem',
                        marginBottom: 0,
                        color: retranslating ? 'var(--amber)' : 'var(--neon-teal)',
                        fontSize: '0.88rem',
                        lineHeight: 1.5,
                    }}
                    data-testid="menu-retranslate-status"
                >
                    {retranslateNote}
                </p>
            )}
        </div>
    );
}
